import { Link, useParams } from 'react-router-dom'

// サンプルデータ
const hospitalData = {
  'hosp-001': {
    id: 'hosp-001',
    hospitalCode: 'H001',
    name: 'Baseクリニック赤坂',
    nameKana: 'ベースクリニックアカサカ',
    shortName: 'Base赤坂',
    hospitalType: 'both',
    membership: 'regular',
    representativeName: '吉岡 紘平',
    postalCode: '107-0052',
    prefecture: '東京都',
    city: '港区',
    address: '赤坂1-1-1',
    building: '赤坂ビル3F',
    email: 'basec.clinic@gmail.com',
    phone: '03-1234-5678',
    fax: '03-1234-5679',
    url: 'https://base-clinic.jp',
    notes: '移植対応可能な医療機関です。',
    isActive: true,
    createdAt: '2023-04-01',
    updatedAt: '2025-11-01',
    doctors: [
      { id: 'doc-001', name: '吉岡 紘平', specialty: '消化器内科', title: '院長' },
      { id: 'doc-009', name: '佐々木 美咲', specialty: '消化器内科', title: '医師' },
    ],
    recentPatients: [
      { id: 'pat-001', researchId: 'R01166', name: 'KuKn', disease: '潰瘍性大腸炎' },
      { id: 'pat-008', researchId: 'R01701', name: 'YmdTky', disease: 'うつ病' },
    ],
  },
}

export default function HospitalDetail() {
  const { id } = useParams()
  const hospital = hospitalData[id as keyof typeof hospitalData] || hospitalData['hosp-001']

  const getHospitalTypeLabel = (type: string) => {
    switch (type) {
      case 'transplant': return '移植医療機関'
      case 'primary': return '主治医療機関'
      case 'both': return '移植・主治医療機関'
      default: return type
    }
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <Link to="/master/hospitals" className="aip-button aip-button--icon">
            <span className="material-symbols-outlined">arrow_back</span>
          </Link>
          <h1 className="aip-main__header-title">病院詳細</h1>
          <div className="aip-main__header-buttons">
            <Link to={`/master/hospitals/${id}/edit`} className="aip-button aip-button--outline">
              <span className="material-symbols-outlined">edit</span>
              編集
            </Link>
          </div>
        </div>
      </header>

      <div className="aip-main__content">
        <div className="detail-container">
          {/* 基本情報 */}
          <section className="detail-section">
            <h2 className="detail-section__title">基本情報</h2>
            <div className="detail-grid">
              <div className="detail-field">
                <label className="detail-field__label">病院コード</label>
                <span className="detail-field__value">{hospital.hospitalCode}</span>
              </div>
              <div className="detail-field">
                <label className="detail-field__label">病院名</label>
                <span className="detail-field__value">{hospital.name}</span>
              </div>
              <div className="detail-field">
                <label className="detail-field__label">病院名（カナ）</label>
                <span className="detail-field__value">{hospital.nameKana}</span>
              </div>
              <div className="detail-field">
                <label className="detail-field__label">略称</label>
                <span className="detail-field__value">{hospital.shortName}</span>
              </div>
              <div className="detail-field">
                <label className="detail-field__label">種別</label>
                <span className="aip-badge aip-badge--info">
                  {getHospitalTypeLabel(hospital.hospitalType)}
                </span>
              </div>
              <div className="detail-field">
                <label className="detail-field__label">会員区分</label>
                <span className="aip-badge aip-badge--primary">
                  {hospital.membership === 'regular' ? '正会員' : '準会員'}
                </span>
              </div>
              <div className="detail-field">
                <label className="detail-field__label">代表者名</label>
                <span className="detail-field__value">{hospital.representativeName}</span>
              </div>
              <div className="detail-field">
                <label className="detail-field__label">状態</label>
                <span className={`aip-badge aip-badge--${hospital.isActive ? 'success' : 'neutral'}`}>
                  {hospital.isActive ? '有効' : '無効'}
                </span>
              </div>
            </div>
          </section>

          {/* 所在地 */}
          <section className="detail-section">
            <h2 className="detail-section__title">所在地</h2>
            <div className="detail-grid">
              <div className="detail-field">
                <label className="detail-field__label">郵便番号</label>
                <span className="detail-field__value">〒{hospital.postalCode}</span>
              </div>
              <div className="detail-field detail-field--full">
                <label className="detail-field__label">住所</label>
                <span className="detail-field__value">
                  {hospital.prefecture}{hospital.city}{hospital.address} {hospital.building}
                </span>
              </div>
            </div>
          </section>

          {/* 連絡先 */}
          <section className="detail-section">
            <h2 className="detail-section__title">連絡先</h2>
            <div className="detail-grid">
              <div className="detail-field">
                <label className="detail-field__label">電話番号</label>
                <a href={`tel:${hospital.phone}`} className="detail-field__link">{hospital.phone}</a>
              </div>
              <div className="detail-field">
                <label className="detail-field__label">FAX番号</label>
                <span className="detail-field__value">{hospital.fax}</span>
              </div>
              <div className="detail-field">
                <label className="detail-field__label">メールアドレス</label>
                <a href={`mailto:${hospital.email}`} className="detail-field__link">{hospital.email}</a>
              </div>
              <div className="detail-field">
                <label className="detail-field__label">URL</label>
                <a href={hospital.url} target="_blank" rel="noopener noreferrer" className="detail-field__link">
                  {hospital.url}
                </a>
              </div>
            </div>
          </section>

          {/* 所属医師 */}
          <section className="detail-section">
            <h2 className="detail-section__title">
              所属医師
              <Link to="/master/doctors" className="section-link">すべて見る</Link>
            </h2>
            <div className="card-list">
              {hospital.doctors.map((doctor) => (
                <Link key={doctor.id} to={`/master/doctors/${doctor.id}`} className="card-item">
                  <div className="card-item__avatar">
                    {doctor.name.charAt(0)}
                  </div>
                  <div className="card-item__info">
                    <span className="card-item__name">{doctor.name}</span>
                    <span className="card-item__meta">{doctor.title} / {doctor.specialty}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* 最近の患者 */}
          <section className="detail-section">
            <h2 className="detail-section__title">
              担当患者（最新）
              <Link to="/patients" className="section-link">すべて見る</Link>
            </h2>
            <div className="card-list">
              {hospital.recentPatients.map((patient) => (
                <Link key={patient.id} to={`/patients/${patient.id}`} className="card-item">
                  <div className="card-item__avatar card-item__avatar--patient">
                    {patient.name.charAt(0)}
                  </div>
                  <div className="card-item__info">
                    <span className="card-item__name">{patient.researchId} - {patient.name}</span>
                    <span className="card-item__meta">{patient.disease}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* 備考 */}
          <section className="detail-section">
            <h2 className="detail-section__title">備考</h2>
            <p className="detail-notes">{hospital.notes || '-'}</p>
          </section>

          {/* システム情報 */}
          <section className="detail-section detail-section--muted">
            <div className="detail-grid detail-grid--compact">
              <div className="detail-field">
                <label className="detail-field__label">登録日</label>
                <span className="detail-field__value">{hospital.createdAt}</span>
              </div>
              <div className="detail-field">
                <label className="detail-field__label">最終更新日</label>
                <span className="detail-field__value">{hospital.updatedAt}</span>
              </div>
            </div>
          </section>
        </div>

        {/* アクションバー */}
        <div className="action-bar">
          <Link to="/master/hospitals" className="aip-button aip-button--outline">
            戻る
          </Link>
          <div className="action-bar__right">
            <button className="aip-button aip-button--outline aip-button--alert">
              <span className="material-symbols-outlined">delete</span>
              削除
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .detail-container {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-lg);
        }

        .detail-section {
          background-color: var(--background-primary);
          padding: var(--spacing-lg);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .detail-section--muted {
          background-color: var(--color-solid-gray25);
        }

        .detail-section__title {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: var(--spacing-md);
          padding-bottom: var(--spacing-sm);
          border-bottom: 1px solid var(--border-color);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .section-link {
          font-size: 0.75rem;
          font-weight: 400;
          color: var(--color-blue600);
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--spacing-md);
        }

        .detail-grid--compact {
          grid-template-columns: repeat(2, 1fr);
        }

        .detail-field {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-xs);
        }

        .detail-field--full {
          grid-column: 1 / -1;
        }

        .detail-field__label {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .detail-field__value {
          font-size: 0.875rem;
          color: var(--text-primary);
        }

        .detail-field__link {
          font-size: 0.875rem;
          color: var(--color-blue600);
        }

        .detail-field__link:hover {
          text-decoration: underline;
        }

        .detail-notes {
          font-size: 0.875rem;
          line-height: 1.6;
        }

        .card-list {
          display: flex;
          flex-wrap: wrap;
          gap: var(--spacing-sm);
        }

        .card-item {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-md);
          background-color: var(--color-solid-gray25);
          border-radius: var(--border-radius-md);
          text-decoration: none;
          color: inherit;
          transition: background-color 0.2s;
        }

        .card-item:hover {
          background-color: var(--color-solid-gray100);
        }

        .card-item__avatar {
          width: 40px;
          height: 40px;
          border-radius: var(--border-radius-md);
          background-color: var(--color-blue500);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }

        .card-item__avatar--patient {
          background-color: var(--color-success);
        }

        .card-item__info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .card-item__name {
          font-size: 0.875rem;
          font-weight: 500;
        }

        .card-item__meta {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .aip-badge {
          display: inline-block;
          padding: 4px 12px;
          font-size: 0.75rem;
          font-weight: 500;
          border-radius: var(--border-radius-sm);
        }

        .aip-badge--primary {
          background-color: var(--color-blue100);
          color: var(--color-blue700);
        }

        .aip-badge--success {
          background-color: var(--color-success-light);
          color: var(--color-success);
        }

        .aip-badge--info {
          background-color: var(--color-blue50);
          color: var(--color-blue600);
        }

        .aip-badge--neutral {
          background-color: var(--color-solid-gray100);
          color: var(--color-solid-gray700);
        }

        .action-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: var(--spacing-lg);
          padding: var(--spacing-md);
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .action-bar__right {
          display: flex;
          gap: var(--spacing-sm);
        }

        .aip-button--alert {
          color: var(--color-alert);
          border-color: var(--color-alert);
        }

        .aip-button--alert:hover {
          background-color: var(--color-alert-light);
        }
      `}</style>
    </>
  )
}




